import React, { useState, useEffect, useCallback } from "react";
import { useGlobal } from "reactn";

import { Section } from "@jsluna/section";

import { trackEvent, NotificationStatuses } from "@jsainsburyplc/id-ui-common";

import { otpService } from "services/otp.service";
import { getErrorMessage } from "utils/otpForm";
import { categoryTypes, actionTypes } from "constants/tracking";
import { DATA_ERROR } from "constants/login";
import {
  newCodeMessage,
  newCodeErrorMessage,
  OTP_FORM_ID,
  MFA_CODE_SENT,
} from "constants/mfaAndOtp";

import PageFormat from "components/PageFormat";
import OtpForm from "components/OtpForm";
import MultiMissionLogo from "components/MultiMissionLogo";

const trackRequestCode = (action) => {
  trackEvent({
    category: categoryTypes.MY_ID_CONFIRMATION_DIFF_PW,
    label: "Send me a new code",
    action,
  });
};

const trackFormSubmit = () => {
  trackEvent({
    category: categoryTypes.MY_ID_CONFIRMATION_DIFF_PW,
    action: actionTypes.SUBMIT,
    label: "Submit and continue",
  });
};

const trackFormSubmitError = (code) => {
  trackEvent({
    category: categoryTypes.MY_ID_CONFIRMATION_DIFF_PW,
    action: actionTypes.ERROR,
    label: `Submit OTP code: ${code}`,
  });
};

const MyIdEmailVerificationPage = () => {
  const rootElem = document.querySelector("#root");
  const errorCode = parseInt(rootElem.getAttribute(DATA_ERROR));

  const [missionName] = useGlobal("readable_mission_name");
  const [requestingNewCode, setRequestingNewCode] = useState(false);
  const [message, setMessage] = useState(
    errorCode ? getErrorMessage(errorCode) : undefined
  );

  const submitHandler = () => {
    const form = document.getElementById(OTP_FORM_ID);
    trackFormSubmit();
    form.submit();
  };

  const requestCode = useCallback(async (showSuccessMessage) => {
    setRequestingNewCode(true);
    try {
      const response = await otpService.requestOtp();
      if (response.ok) {
        trackRequestCode(actionTypes.SUCCESS);
        sessionStorage.setItem(MFA_CODE_SENT, "true");
        setRequestingNewCode(false);
        if (showSuccessMessage) {
          setMessage({
            type: NotificationStatuses.SUCCESS_TYPE,
            text: newCodeMessage,
          });
        }
      } else {
        throw Error;
      }
    } catch (error) {
      trackRequestCode(actionTypes.ERROR);
      setRequestingNewCode(false);
      setMessage({
        type: NotificationStatuses.ERROR_TYPE,
        text: newCodeErrorMessage,
      });
    }
  }, []);

  const handleRequestNewCode = async (event) => {
    event && event.preventDefault();
    trackRequestCode(actionTypes.CODE_REQUESTED);
    requestCode(true);
  };

  useEffect(() => {
    const isCodeSent = sessionStorage.getItem(MFA_CODE_SENT) === "true";
    !isCodeSent && requestCode();
  }, [requestCode]);

  useEffect(() => {
    if (message) {
      trackFormSubmitError(errorCode);
    }
  }, [message, errorCode]);

  return (
    <PageFormat>
      <Section
        className="c-hero c-hero--center"
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "row",
        }}
      >
        <MultiMissionLogo />
      </Section>
      <h1 className="ln-u-h4">We need to verify your email address</h1>
      <p className="ln-u-margin-bottom*3">
        {missionName} is now using My ID, meaning you can use the same email and
        password to log in to Nectar and Sainsbury’s Groceries.
      </p>
      <p className="ln-u-margin-bottom*4">
        To get started, we’ve sent a code to your email to verify your email
        address. Please enter the code to continue logging in:
      </p>

      <OtpForm
        onRequestCode={handleRequestNewCode}
        onSubmit={submitHandler}
        isRequestingCode={requestingNewCode}
        notification={message}
        isIovationEnabled={process.env.REACT_APP_IOVATION_ENABLED}
      />
    </PageFormat>
  );
};

export default MyIdEmailVerificationPage;
